import type { OnDestroy, OnInit } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';

import { FsAxonDevice, FsPresenceDevice } from '@lib/api/entities';
import { SnCurrentUser } from '@lib/auth/services/current-user.service';
import { IDeviceMarker } from '@lib/axon/interfaces/device-marker.interface';
import { IDevice } from '@lib/axon/interfaces/device.interface';
import { SnAspectRatio } from '@lib/common/enums';

import { AxonDeviceModels, PresenceDeviceModels } from '../../models';
import { FsAxonService } from '../../services';

@Component({
  selector: 'fs-axon-livestream-iframe',
  templateUrl: './axon-livestream-iframe.component.html',
  styleUrls: ['./axon-livestream-iframe.component.scss'],
})
export class AxonLivestreamIframeComponent implements OnDestroy, OnInit {
  @Input() public device: IDevice;

  @Input()
  public actions: boolean;

  @Input()
  public isDocked: boolean;

  @Input()
  public marker: IDeviceMarker;

  @Output()
  public readonly closeCamera = new EventEmitter<any>();

  public iFrame: HTMLIFrameElement;
  public deviceInstance: IDevice;

  private _destroy$ = new Subject<void>();

  constructor(
    public readonly axonService: FsAxonService,
    private readonly _currentUser: SnCurrentUser,
    private readonly _element: ElementRef,
  ) {
    this._setupListeners();
  }

  public ngOnInit(): void {
    this.deviceInstance = this.device;
    this.createIframe(this.deviceInstance);
  }

  public ngOnDestroy(): void {
    const index = this.axonService.activeIFrames.findIndex((active) => {
      return active.iFrame === this.iFrame;
    });
    this.axonService.activeIFrames.splice(index, 1);
    this._destroy$.next();
    this._destroy$.complete();
  }

  public createIframe(deviceInstance: IDevice): void {
    const searchParams = new URLSearchParams({
      deviceId: deviceInstance.axonDeviceId,
      agencyId: deviceInstance.partnerId,
      autoPlay: 'true',
    });
    const url = `${this.axonService.publicGatewayUrl}/livestream-player?${searchParams.toString()}`;

    const container = this._element.nativeElement.querySelector('.iframeContainer');
    const aspectRatio = this._determineAspectRatio();
    this.iFrame = document.createElement('iframe');
    this.iFrame.src = url;
    this.iFrame.allow = 'microphone; fullscreen';
    this.iFrame.style.width = '100%';
    this.iFrame.style.height = '100%';
    this.iFrame.setAttribute('data-testid', 'streaming-iframe');
    this.iFrame.setAttribute('disable-fullscreen-overlays', 'true');
    container.style.paddingBottom = `${aspectRatio}%`;
    container.appendChild(this.iFrame);
    this.iFrame.onload = () => {
      const command = {
        action: 'ACCESS_TOKEN_UPDATED',
        accessToken: this._currentUser.settings.authTokens?.axonconnect?.accessToken,
      };
      this.iFrame.contentWindow.postMessage(command, '*');
    };
    this.axonService.addAnalytics('axon_livestream_invoked', {
      device: deviceInstance.filteredAnaltyicData,
    });

    this.axonService.activeIFrames.push({
      iFrame: this.iFrame,
      axonDeviceId: deviceInstance.axonDeviceId,
      partnerId: deviceInstance.partnerId,
    });
  }

  public refreshToken(): void {
    const command = {
      action: 'ACCESS_TOKEN_UPDATED',
      accessToken: this._currentUser.settings.authTokens?.axonconnect?.accessToken,
    };
    this.iFrame.contentWindow.postMessage(command, '*');
  }

  public followMode(): void {
    const isFollowing = this.axonService.deviceInFollowMode !== this.device;
    if (isFollowing) {
      this.axonService.enterFollowMode(this.device);
    } else {
      this.axonService.leaveFollowMode(this.device);
    }
    this.axonService.iFrameFM(isFollowing ? this.device : null);
    this.marker?.setFollowMode(isFollowing);
  }

  public deviceHistory(): void {
    this.axonService.openDeviceHistory(this.deviceInstance);
  }

  public openPopout(): void {
    this.axonService.openPopout(this.device);
    this.closeCamera.next();
    this.axonService.addAnalytics('axon_docked_to_popout', {
      axonDevice: this.device.filteredAnaltyicData,
    });
  }

  private _determineAspectRatio(): number {
    // Map presence device models to axon device models for consistent handling
    const deviceModel = this._mapToAxonDeviceModel(this.device.deviceModel);
    
    switch (deviceModel) {
      case AxonDeviceModels.AxonBody4:
      case AxonDeviceModels.AxonBodyWorkforce:
      case AxonDeviceModels.AxonBodyWorkforceMini:
      case AxonDeviceModels.AxonFleet3Hub:
        return SnAspectRatio.Standard;
      case AxonDeviceModels.AxonBody3:
        return SnAspectRatio.HD;
      default:
        return SnAspectRatio.Standard;
    }
  }

  /**
   * Maps presence device models to their corresponding axon device models
   * This is needed because presence devices use lowercase model names (e.g., 'abwmini')
   * while axon devices use uppercase constants (e.g., 'AXON_BODY_WORKFORCE_MINI')
   */
  private _mapToAxonDeviceModel(deviceModel: string): string {
    switch (deviceModel) {
      case PresenceDeviceModels.AB3:
        return AxonDeviceModels.AxonBody3;
      case PresenceDeviceModels.AB4:
        return AxonDeviceModels.AxonBody4;
      case PresenceDeviceModels.ABW:
        return AxonDeviceModels.AxonBodyWorkforce;
      case PresenceDeviceModels.ABW_MINI:
        return AxonDeviceModels.AxonBodyWorkforceMini;
      case PresenceDeviceModels.Fleet3:
        return AxonDeviceModels.AxonFleet3Hub;
      default:
        // If it's already an axon device model, return as-is
        return deviceModel;
    }
  }

  private _setupListeners(): void {
    this.axonService.iFrameFM$.pipe(takeUntil(this._destroy$)).subscribe((device) => {
      if (device) {
        this.axonService.deviceInFollowMode = device;
      }
      const isActive = !!device && device.axonDeviceId === this.device?.axonDeviceId;
      this.marker?.setFollowMode(isActive);
    });
  }
}
